import { motion } from 'framer-motion'
import { GitCommit, Sparkles, Bug, Zap, Download, Calendar, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocale } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'
import { useApi } from '../hooks/useApi'
import { changelogApi } from '../lib/api'

const LATEST_VERSION = '2.0.1'
const LATEST_DATE = '2026-03-28'

const getChangeIcon = (type: string) => {
  switch (type) {
    case 'feature':
      return <Sparkles className="w-4 h-4 text-accent" />
    case 'improvement':
      return <Zap className="w-4 h-4 text-primary" />
    case 'fix':
      return <Bug className="w-4 h-4 text-success" />
    default:
      return <GitCommit className="w-4 h-4 text-gray-500" />
  }
}

const getVersionBadge = (type: string) => {
  switch (type) {
    case 'major':
      return 'bg-accent/20 text-accent'
    case 'minor':
      return 'bg-primary/20 text-primary'
    case 'patch':
      return 'bg-success/20 text-success'
    default:
      return 'bg-gray-700 text-gray-400'
  }
}

export default function Changelog() {
  const { t } = useTranslation(['changelog', 'common'])
  const { htmlLang } = useLocale()
  const { data: entries } = useApi(() => changelogApi.getAll(), [])
  const releases = entries || []
  const [expandedRelease, setExpandedRelease] = useState<string | null>(LATEST_VERSION)

  const getChangeText = (type: string) => {
    switch (type) {
      case 'feature':
        return t('changeType.feature')
      case 'improvement':
        return t('changeType.improvement')
      case 'fix':
        return t('changeType.fix')
      default:
        return t('changeType.default')
    }
  }

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString(htmlLang, { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/changelog"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.changelog'), url: '/changelog' }])}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <GitCommit className="w-4 h-4" />
            {t('badge')}
          </span>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            {t('title')}
          </h1>
          <p className="text-gray-400 text-lg">
            {t('text')}
          </p>
        </motion.div>

        {/* Latest Version CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-6 mb-12"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Download className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t('latest.title', { version: LATEST_VERSION })}</h2>
                <p className="text-gray-400 text-sm">{t('latest.date', { date: formatDate(LATEST_DATE) })}</p>
              </div>
            </div>
            <button className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-colors flex items-center gap-2">
              <Download className="w-4 h-4" />
              {t('latest.download')}
            </button>
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-transparent hidden md:block" />

          <div className="space-y-6">
            {releases.map((release, idx) => (
              <motion.div
                key={release.version}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                {/* Timeline dot */}
                <div className="absolute left-5 w-3 h-3 rounded-full bg-primary hidden md:block" />

                <div className="md:ml-12">
                  <div className="glass rounded-2xl overflow-hidden">
                    <button
                      onClick={() => setExpandedRelease(expandedRelease === release.version ? null : release.version)}
                      className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-xl font-bold text-white">v{release.version}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getVersionBadge(release.type)}`}>
                              {release.type === 'major' ? t('releaseType.major') : release.type === 'minor' ? t('releaseType.minor') : t('releaseType.patch')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(release.date)}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${expandedRelease === release.version ? 'rotate-90' : ''}`} />
                    </button>

                    {expandedRelease === release.version && (
                      <div className="px-6 pb-6 border-t border-white/5">
                        <ul className="space-y-3 mt-4">
                          {release.items.map((change: { type: string; text: string }, changeIdx: number) => (
                            <li key={changeIdx} className="flex items-start gap-3">
                              {getChangeIcon(change.type)}
                              <div>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                  change.type === 'feature' ? 'bg-accent/20 text-accent' :
                                  change.type === 'improvement' ? 'bg-primary/20 text-primary' :
                                  'bg-success/20 text-success'
                                }`}>
                                  {getChangeText(change.type)}
                                </span>
                                <p className="text-gray-300 mt-1">{change.text}</p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </motion.div>
  )
}
